import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { dataLayer, utilities } from '@weatherbug/common';
import { reduxConnect } from '../../common';
import { mediumMediaQuery, smallMediaQuery } from '../../common/utilities/sizeHelpers';

import { IStdParameters, IStoreState } from '../common/interfaces/types';

import { HurricaneWidgetMapView } from './hurricaneMapView';
import { HurricaneWidgetCaptionView } from './hurricaneWidgetCaptionView';
import { GetHurricaneUriPath, isCached, refresh, verbose } from './constants';
import { useIntersectionObserver } from '../common/useIntersectionObserver';

const { getHurricaneByLatLon, getHurricaneOutlookSummary } = dataLayer.actions;
const { pressureUnitsEnum, speedUnitsEnum  } = utilities.units;

interface HurricaneWidgetProps {
    pageId: string;
    stdParameters: IStdParameters;
    latitude: number;
    longitude: number;
    hurricaneBaseUrl: string;
    hurricaneOutlookBaseUrl: string;
    authId: string;
    secret: string;
    pressureUnit: keyof typeof pressureUnitsEnum;
    speedUnit: keyof typeof speedUnitsEnum;
    getHurricaneOutlookSummary({
        includeForecast7d,
        latitude,
        longitude,
        forceRefreshOfCachedData,
        baseUrl,
        authId,
        secret,
        stdApiParameters,
        verboseResponseKeys 
    }: {
            includeForecast7d: boolean;
            latitude: number;
            longitude: number;
            forceRefreshOfCachedData: boolean;
            baseUrl: string;
            authId: string;
            secret: string;
            stdApiParameters: IStdParameters;
            verboseResponseKeys: boolean;
        }): void;
    getHurricaneByLatLon(latitude: number,
        longitude: number,
        isCached: boolean,
        hurricaneBaseUrl: string,
        getHurricaneUriPath: typeof GetHurricaneUriPath,
        authId: string,
        secret: string,
        stdApiParameters: IStdParameters,
        unitParameters: {
            pressureUnit: keyof typeof pressureUnitsEnum;
            speedUnit: keyof typeof speedUnitsEnum;
        },
        verbose: boolean,
        refresh: boolean): void;
    
}

const mapStoreStateToProps = (storeState: IStoreState) => {
    const { units, appState } = storeState || {};

    const { user, stdParameters, secret, authId, hurricaneBaseUrl, hurricaneOutlookBaseUrl, pageId } = appState?.appState || {}; 

    const { pressureUnit, windUnit: speedUnit } = units || {};

    const { Latitude: latitude, Longitude: longitude } = user?.ActiveLocation?.Locality || {};

    return {
        pageId,
        stdParameters,
        secret,
        authId,
        hurricaneBaseUrl,
        hurricaneOutlookBaseUrl,
        latitude,
        longitude, 
        pressureUnit,
        speedUnit
    };
};

const mapDispatchToProps = {
    getHurricaneByLatLon,
    getHurricaneOutlookSummary
};

export const HurricaneWidget = reduxConnect(mapStoreStateToProps, mapDispatchToProps)(({ 
    getHurricaneByLatLon,
    getHurricaneOutlookSummary,
    pageId,
    latitude,
    longitude,
    authId,
    secret,
    hurricaneBaseUrl,
    hurricaneOutlookBaseUrl,
    stdParameters,
    speedUnit,
    pressureUnit }: HurricaneWidgetProps): JSX.Element => {
    
    const [hurricaneWidgetDidMount, setHurricaneWidgetDidMount] = useState(false);
    const widgetRef = useRef(null);

    const isAlertsHurricanePage = pageId === 'alerts/hurricane'; //  Loading widget map when on on alerts/hurricane page - since we load same map on page.
    const isWidgetVisible = isAlertsHurricanePage ? isAlertsHurricanePage : useIntersectionObserver(widgetRef, '330px'); // load fire widget body when it's ~330px from the view port

    useEffect(() => {
        if (isWidgetVisible && !hurricaneWidgetDidMount) {
            setHurricaneWidgetDidMount(true);
        }
    }, [isWidgetVisible, hurricaneWidgetDidMount]);

    useEffect(() => {
        if (Number.isFinite(latitude) && Number.isFinite(longitude) && authId && secret && hurricaneBaseUrl && hurricaneOutlookBaseUrl && hurricaneWidgetDidMount && speedUnit && pressureUnit) {
            const unitParameters = {
                pressureUnit,
                speedUnit
            };
            
            Promise.allSettled([
                getHurricaneByLatLon(latitude, 
                    longitude, 
                    isCached, 
                    hurricaneBaseUrl, 
                    GetHurricaneUriPath, 
                    authId, 
                    secret,
                    stdParameters,
                    unitParameters,
                    verbose,
                    refresh),
                getHurricaneOutlookSummary(
                    {
                        includeForecast7d: true,
                        latitude, 
                        longitude, 
                        forceRefreshOfCachedData: isCached,
                        baseUrl: hurricaneOutlookBaseUrl, // TODO: WIP Update config files to use non pulsedev BaseURLS after Backend team deploys Hurricane Outlook API to Prod.
                        authId, 
                        secret,
                        stdApiParameters: stdParameters,
                        verboseResponseKeys: verbose
                    })
            ]);
        }
    }, [latitude, longitude, authId, secret, hurricaneBaseUrl, hurricaneOutlookBaseUrl, stdParameters, pageId, speedUnit, pressureUnit, hurricaneWidgetDidMount]);
    
    return (
        <Container ref={widgetRef}>
            <HurricaneWidgetMapView isHUrricaneWidgetMap={true} widgetIsLazyLoaded={hurricaneWidgetDidMount} />
            <HurricaneWidgetCaptionView  />
        </Container>
    );
});

const Container = styled.figure`
    display: flex;
    flex-direction: column;
    height: 283px;

    @media ${smallMediaQuery} {
        height: 133px;
    }

    @media ${mediumMediaQuery} {
        height: 158px;
    }

    margin: 0;
    padding: 0;
`;

export { HurricaneWidget as default };
